import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Upload, FileText, Calculator, Loader2, AlertCircle, Layers, Square, CornerDownRight, Building2, Plus, Trash2, Edit3, Check } from 'lucide-react';
import { API_URL } from '../config';


const ROOM_COLORS = ['#e74c3c', '#2980b9', '#27ae60', '#8e44ad', '#e67e22', '#1abc9c', '#f39c12', '#e84393', '#0984e3', '#6c5ce7'];

const uiText = {
  ru: {
    title: 'Калькулятор панелей', subtitle: 'Загрузите чертёж — AI найдёт помещения, а вы проверите и скорректируете',
    uploadLabel: 'Загрузите PDF-чертёж', uploadHint: 'Перетащите файл или нажмите', uploadFormats: 'PDF',
    panelHeight: 'Высота панели', panelWidth: 'Ширина панели', panelWidthMm: 'мм',
    calculate: 'Рассчитать', calculating: 'Анализ чертежа... (~30 сек)',
    results: 'Результаты расчёта', room: 'Помещение', walls: 'Стены', ceilingH: 'Высота потолка',
    wallArea: 'Площадь стен', openings: 'Проёмы', netArea: 'Чистая площадь',
    panelsFull: 'Панели', panelLayout: 'Раскладка по высоте',
    totals: 'Итого', totalPanels: 'Всего панелей',
    jointProfiles: 'Стыковочные профили', cornerProfiles: 'Угловые профили',
    plinth: 'Плинтус', topMolding: 'Верхний молдинг',
    error: 'Ошибка анализа.', door: 'Дверь', window: 'Окно', wallLen: 'Длина', panels: 'Панели',
    pcs: 'шт.', m: 'м', m2: 'м²',
    floorLabel: 'Этаж', floorPlaceholder: 'Например: 1 этаж',
    roomTypeLabel: 'Тип помещений', roomTypePlaceholder: 'Например: кабинеты',
    reserveLabel: 'Запас', reservePanels: 'Запас панелей', reserveProfiles: 'Запас профилей', totalWithReserve: 'Итого с запасом',
    addRoom: 'Добавить помещение', deleteRoom: 'Удалить', editHint: 'Нажмите на значение чтобы изменить',
    addWall: '+ стена', wallWidth: 'Ш', wallHeight: 'Д',
  },
  kz: {
    title: 'Панель калькуляторы', subtitle: 'PDF жүктеңіз — AI бөлмелерді табады, сіз тексеріп түзетесіз',
    uploadLabel: 'PDF жүктеңіз', uploadHint: 'Файлды сүйреңіз', uploadFormats: 'PDF',
    panelHeight: 'Панель биіктігі', panelWidth: 'Панель ені', panelWidthMm: 'мм',
    calculate: 'Есептеу', calculating: 'Талдау... (~30 сек)',
    results: 'Нәтижелер', room: 'Бөлме', walls: 'Қабырғалар', ceilingH: 'Төбе биіктігі',
    wallArea: 'Аудан', openings: 'Ойықтар', netArea: 'Таза аудан',
    panelsFull: 'Панельдер', panelLayout: 'Орналасу',
    totals: 'Барлығы', totalPanels: 'Барлық панельдер',
    jointProfiles: 'Қосылу профильдері', cornerProfiles: 'Бұрыш профильдері',
    plinth: 'Плинтус', topMolding: 'Жоғарғы молдинг',
    error: 'Қате.', door: 'Есік', window: 'Терезе', wallLen: 'Ұзындық', panels: 'Панельдер',
    pcs: 'дана', m: 'м', m2: 'м²',
    floorLabel: 'Қабат', floorPlaceholder: '1 қабат',
    roomTypeLabel: 'Бөлме түрі', roomTypePlaceholder: 'кабинеттер',
    reserveLabel: 'Қор', reservePanels: 'Панель қоры', reserveProfiles: 'Профиль қоры', totalWithReserve: 'Қормен барлығы',
    addRoom: 'Бөлме қосу', deleteRoom: 'Жою', editHint: 'Өзгерту үшін мәнді басыңыз',
    addWall: '+ қабырға', wallWidth: 'Е', wallHeight: 'Ұ',
  },
  en: {
    title: 'Panel Calculator', subtitle: 'Upload a blueprint — AI finds rooms, you review and correct',
    uploadLabel: 'Upload PDF', uploadHint: 'Drag & drop or click', uploadFormats: 'PDF',
    panelHeight: 'Panel Height', panelWidth: 'Panel Width', panelWidthMm: 'mm',
    calculate: 'Calculate', calculating: 'Analyzing... (~30 sec)',
    results: 'Results', room: 'Room', walls: 'Walls', ceilingH: 'Ceiling Height',
    wallArea: 'Wall Area', openings: 'Openings', netArea: 'Net Area',
    panelsFull: 'Panels', panelLayout: 'Height Layout',
    totals: 'Total', totalPanels: 'Total Panels',
    jointProfiles: 'Joint Profiles', cornerProfiles: 'Corner Profiles',
    plinth: 'Plinth', topMolding: 'Top Molding',
    error: 'Error.', door: 'Door', window: 'Window', wallLen: 'Length', panels: 'Panels',
    pcs: 'pcs', m: 'm', m2: 'm²',
    floorLabel: 'Floor', floorPlaceholder: '1st floor',
    roomTypeLabel: 'Room Type', roomTypePlaceholder: 'offices',
    reserveLabel: 'Reserve', reservePanels: 'Reserve panels', reserveProfiles: 'Reserve profiles', totalWithReserve: 'Total with reserve',
    addRoom: 'Add room', deleteRoom: 'Delete', editHint: 'Click value to edit',
    addWall: '+ wall', wallWidth: 'W', wallHeight: 'L',
  },
  ar: {
    title: 'حاسبة الألواح', subtitle: 'ارفع مخططاً — يكتشف الذكاء الاصطناعي الغرف، وأنت تراجع وتصحح',
    uploadLabel: 'رفع PDF', uploadHint: 'اسحب وأفلت أو انقر', uploadFormats: 'PDF',
    panelHeight: 'ارتفاع اللوح', panelWidth: 'عرض اللوح', panelWidthMm: 'مم',
    calculate: 'احسب', calculating: 'جارٍ التحليل... (~30 ثانية)',
    results: 'النتائج', room: 'الغرفة', walls: 'الجدران', ceilingH: 'ارتفاع السقف',
    wallArea: 'مساحة الجدار', openings: 'الفتحات', netArea: 'المساحة الصافية',
    panelsFull: 'الألواح', panelLayout: 'توزيع الارتفاع',
    totals: 'المجموع', totalPanels: 'إجمالي الألواح',
    jointProfiles: 'بروفيلات الوصلات', cornerProfiles: 'بروفيلات الزوايا',
    plinth: 'قاعدة الجدار', topMolding: 'قالب العلوي',
    error: 'خطأ.', door: 'باب', window: 'نافذة', wallLen: 'الطول', panels: 'ألواح',
    pcs: 'قطعة', m: 'م', m2: 'م²',
    floorLabel: 'الطابق', floorPlaceholder: 'الطابق الأول',
    roomTypeLabel: 'نوع الغرفة', roomTypePlaceholder: 'مكاتب',
    reserveLabel: 'الاحتياطي', reservePanels: 'ألواح احتياطية', reserveProfiles: 'بروفيلات احتياطية', totalWithReserve: 'المجموع مع الاحتياطي',
    addRoom: 'إضافة غرفة', deleteRoom: 'حذف', editHint: 'انقر على القيمة للتعديل',
    addWall: '+ جدار', wallWidth: 'ع', wallHeight: 'ط',
  }
};

// Recalculate panels for a single room
const calcRoom = (room, panelHeight, panelWidth) => {
  const panelW = panelWidth;
  const available = [3000, 2700, 2500];
  const ceilingMm = Math.round((room.ceiling_height_m || panelHeight) * 1000);
  const rows = [];
  let rem = ceilingMm;
  while (rem > 0) {
    let chosen = null;
    for (const h of available) { if (h <= rem) { chosen = h; break; } }
    if (!chosen) chosen = Math.min(rem, 2500);
    rows.push(chosen);
    rem -= chosen;
  }

  let totalPanels = 0;
  let wallArea = 0;
  let openingArea = 0;
  const wallResults = room.walls.map(w => {
    const len = w.length_m || 0;
    const area = len * (room.ceiling_height_m || panelHeight);
    let opArea = 0;
    if (w.has_door) opArea += (w.door_width_m || 0.9) * (w.door_height_m || 2.1);
    if (w.has_window) opArea += (w.window_width_m || 1.5) * (w.window_height_m || 1.2);
    const panels = Math.ceil(len / panelW) * rows.length;
    totalPanels += panels;
    wallArea += area;
    openingArea += opArea;
    return { ...w, panels, area_m2: Math.round(area * 100) / 100, net_area_m2: Math.round((area - opArea) * 100) / 100 };
  });

  return {
    ...room,
    walls: wallResults,
    panels_full: totalPanels,
    wall_area_m2: Math.round(wallArea * 100) / 100,
    opening_area_m2: Math.round(openingArea * 100) / 100,
    net_area_m2: Math.round((wallArea - openingArea) * 100) / 100,
    panel_layout: rows.map(h => h / 1000),
    corner_count: room.walls.length,
  };
};

const EditableCell = ({ value, onChange, type = 'number', step = 0.1, min = 0, className = '' }) => {
  const [editing, setEditing] = useState(false);
  const [tempVal, setTempVal] = useState(value);
  const inputRef = useRef(null);

  const startEdit = () => { setTempVal(value); setEditing(true); setTimeout(() => inputRef.current?.select(), 50); };
  const confirm = () => {
    setEditing(false);
    const v = type === 'number' ? parseFloat(tempVal) || 0 : tempVal;
    if (v !== value) onChange(v);
  };

  if (editing) {
    return (
      <input ref={inputRef} type={type} step={step} min={min} value={tempVal}
        onChange={e => setTempVal(e.target.value)}
        onBlur={confirm} onKeyDown={e => { if (e.key === 'Enter') confirm(); if (e.key === 'Escape') setEditing(false); }}
        className={`bg-white border border-[#8b4444] rounded px-1.5 py-0.5 text-sm w-20 focus:outline-none ${className}`}
        autoFocus />
    );
  }
  return (
    <span onClick={startEdit} className={`cursor-pointer hover:bg-[#8b4444]/10 px-1.5 py-0.5 rounded transition-colors ${className}`}>
      {typeof value === 'number' ? value : value}
    </span>
  );
};

const PanelCalculator = () => {
  const { language } = useLanguage();
  const t = uiText[language] || uiText.ru;
  const fileInputRef = useRef(null);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [panelHeight, setPanelHeight] = useState(3.0);
  const [panelWidth, setPanelWidth] = useState(1.2);
  const [floorFilter, setFloorFilter] = useState('');
  const [roomTypeFilter, setRoomTypeFilter] = useState('');
  const [reserve, setReserve] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rooms, setRooms] = useState(null); // editable rooms array

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
    if (files.length > 0) { setSelectedFiles(prev => [...prev, ...files]); setError(null); setRooms(null); }
  };
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type === 'application/pdf');
    if (files.length > 0) { setSelectedFiles(prev => [...prev, ...files]); setError(null); setRooms(null); }
  };
  const removeFile = (idx) => { setSelectedFiles(prev => prev.filter((_, i) => i !== idx)); setRooms(null); };

  // Recalculated totals from editable rooms
  const calculated = useMemo(() => {
    if (!rooms) return null;
    const recalced = rooms.map(r => calcRoom(r, panelHeight, panelWidth));
    const totals = { total_panels: 0, total_wall_area_m2: 0, total_opening_area_m2: 0, total_net_area_m2: 0, joint_profiles_m: 0, corner_profiles_inner: 0, plinth_m: 0, top_molding_m: 0 };
    recalced.forEach(rm => {
      totals.total_panels += rm.panels_full;
      totals.total_wall_area_m2 += rm.wall_area_m2;
      totals.total_opening_area_m2 += rm.opening_area_m2;
      totals.total_net_area_m2 += rm.net_area_m2;
      const perim = rm.walls.reduce((s, w) => s + (w.length_m || 0), 0);
      totals.joint_profiles_m += Math.ceil(perim / panelWidth) * rm.ceiling_height_m;
      totals.corner_profiles_inner += rm.corner_count;
      totals.plinth_m += perim;
      totals.top_molding_m += perim;
    });
    for (const k of Object.keys(totals)) totals[k] = Math.round(totals[k] * 10) / 10;
    totals.joint_profiles_pcs = Math.ceil(totals.joint_profiles_m / 3);
    totals.plinth_pcs = Math.ceil(totals.plinth_m / 3);
    totals.top_molding_pcs = Math.ceil(totals.top_molding_m / 3);
    return { rooms: recalced, totals };
  }, [rooms, panelHeight, panelWidth]);

  const updateRoom = useCallback((roomIdx, field, value) => {
    setRooms(prev => prev.map((r, i) => i === roomIdx ? { ...r, [field]: value } : r));
  }, []);

  const updateWall = useCallback((roomIdx, wallIdx, field, value) => {
    setRooms(prev => prev.map((r, i) => {
      if (i !== roomIdx) return r;
      const newWalls = r.walls.map((w, wi) => wi === wallIdx ? { ...w, [field]: value } : w);
      return { ...r, walls: newWalls };
    }));
  }, []);

  const addWall = useCallback((roomIdx) => {
    setRooms(prev => prev.map((r, i) => {
      if (i !== roomIdx) return r;
      return { ...r, walls: [...r.walls, { length_m: 3.0, has_door: false, door_width_m: 0.9, door_height_m: 2.1, has_window: false, window_width_m: 1.5, window_height_m: 1.2 }] };
    }));
  }, []);

  const deleteWall = useCallback((roomIdx, wallIdx) => {
    setRooms(prev => prev.map((r, i) => {
      if (i !== roomIdx) return r;
      return { ...r, walls: r.walls.filter((_, wi) => wi !== wallIdx) };
    }));
  }, []);

  const addRoom = useCallback(() => {
    setRooms(prev => [...(prev || []), {
      name: language === 'ru' ? 'Новое помещение' : 'New room',
      walls: [{ length_m: 5.0, has_door: false, door_width_m: 0.9, door_height_m: 2.1, has_window: false, window_width_m: 1.5, window_height_m: 1.2 },
              { length_m: 4.0, has_door: false, door_width_m: 0.9, door_height_m: 2.1, has_window: false, window_width_m: 1.5, window_height_m: 1.2 },
              { length_m: 5.0, has_door: true, door_width_m: 0.9, door_height_m: 2.1, has_window: false, window_width_m: 1.5, window_height_m: 1.2 },
              { length_m: 4.0, has_door: false, door_width_m: 0.9, door_height_m: 2.1, has_window: false, window_width_m: 1.5, window_height_m: 1.2 }],
      ceiling_height_m: panelHeight,
    }]);
  }, [panelHeight, language]);

  const deleteRoom = useCallback((roomIdx) => {
    setRooms(prev => prev.filter((_, i) => i !== roomIdx));
  }, []);

  const uploadFileChunked = async (file) => {
    const CHUNK_SIZE = 1024 * 1024;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadId = '';
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
      const formData = new FormData();
      formData.append('file', chunk, `chunk_${i}`);
      const res = await fetch(`${API_URL}/api/calculator/upload-chunk?chunk_index=${i}&total_chunks=${totalChunks}&upload_id=${uploadId}`, { method: 'POST', body: formData });
      const text = await res.text();
      let data; try { data = JSON.parse(text); } catch { throw new Error('Upload error'); }
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      uploadId = data.upload_id;
    }
    return uploadId;
  };

  const pollForResult = async (jobId) => {
    for (let i = 0; i < 60; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const sr = await fetch(`${API_URL}/api/calculator/status/${jobId}`);
        const st = await sr.text();
        let sd; try { sd = JSON.parse(st); } catch { continue; }
        if (sd.status === 'done') return sd.results;
        if (sd.status === 'error') throw new Error(sd.error || 'Error');
      } catch (pe) { if (pe.message && !pe.message.includes('Failed to fetch')) throw pe; }
    }
    throw new Error('Timeout');
  };

  const handleCalculate = async () => {
    if (selectedFiles.length === 0) return;
    setLoading(true); setError(null); setRooms(null);
    try {
      const allRooms = [];
      for (const file of selectedFiles) {
        const uploadId = await uploadFileChunked(file);
        const params = new URLSearchParams({ upload_id: uploadId, filename: file.name, panel_height: panelHeight.toString() });
        if (floorFilter.trim()) params.set('floor_filter', floorFilter.trim());
        if (roomTypeFilter.trim()) params.set('room_type_filter', roomTypeFilter.trim());
        const startRes = await fetch(`${API_URL}/api/calculator/start-analysis?${params}`, { method: 'POST' });
        const startText = await startRes.text();
        let startData; try { startData = JSON.parse(startText); } catch { throw new Error('Server error'); }
        if (!startRes.ok) throw new Error(startData.detail || 'Failed');
        const result = await pollForResult(startData.job_id);
        allRooms.push(...(result.rooms || []));
      }
      // Set raw rooms for editing (strip calculated fields, keep AI data)
      setRooms(allRooms.map(r => ({
        name: r.name,
        ceiling_height_m: r.ceiling_height_m || panelHeight,
        walls: (r.walls || []).map(w => ({
          length_m: w.length_m || 0, has_door: !!w.has_door, door_width_m: w.door_width_m || 0.9, door_height_m: w.door_height_m || 2.1,
          has_window: !!w.has_window, window_width_m: w.window_width_m || 1.5, window_height_m: w.window_height_m || 1.2,
        })),
      })));
    } catch (e) { setError(e.message || t.error); } finally { setLoading(false); }
  };

  return (
    <section className="py-16 bg-[#F5F5F0]" data-testid="panel-calculator">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#061d36]/10 rounded-full mb-4">
            <Calculator className="w-5 h-5 text-[#061d36]" />
            <span className="text-[#061d36] font-medium text-sm uppercase tracking-wider">{t.title}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#061d36] mb-3" data-testid="calculator-title">{t.title}</h2>
          <p className="text-[#061d36]/60 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Upload + Settings */}
          <div className="space-y-6">
            <div className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${selectedFiles.length > 0 ? 'border-[#8b4444] bg-[#8b4444]/5' : 'border-[#061d36]/20 hover:border-[#061d36]/40 bg-white'}`}
              onClick={() => fileInputRef.current?.click()} onDrop={handleDrop} onDragOver={e => e.preventDefault()} data-testid="file-upload-zone">
              <input ref={fileInputRef} type="file" accept=".pdf" multiple className="hidden" onChange={handleFileChange} />
              {selectedFiles.length > 0 ? (
                <div className="space-y-2">
                  {selectedFiles.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-5 h-5 text-[#8b4444] flex-shrink-0" />
                        <span className="text-[#061d36] text-sm font-medium truncate">{f.name}</span>
                        <span className="text-[#061d36]/40 text-xs flex-shrink-0">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                      </div>
                      <button onClick={e => { e.stopPropagation(); removeFile(idx); }} className="text-red-400 hover:text-red-600 p-1"><AlertCircle className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <><Upload className="w-10 h-10 text-[#061d36]/30 mx-auto mb-3" /><p className="text-[#061d36] font-medium mb-1">{t.uploadLabel}</p><p className="text-[#061d36]/40 text-sm">{t.uploadHint}</p></>
              )}
            </div>

            <div>
              <h3 className="text-[#061d36] font-medium mb-3">{t.panelHeight}</h3>
              <div className="flex gap-3">
                {[2.5, 2.7, 3.0].map(h => (
                  <button key={h} onClick={() => setPanelHeight(h)} className={`flex-1 py-3 rounded-xl font-semibold transition-all border-2 ${panelHeight === h ? 'border-[#8b4444] bg-[#8b4444] text-white' : 'border-[#061d36]/20 text-[#061d36] hover:border-[#061d36]/40 bg-white'}`}>{h} {t.m}</button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-[#061d36] font-medium mb-3">{t.panelWidth}</h3>
              <div className="flex gap-3">
                {[{ label: '1000', val: 1.0 }, { label: '1200', val: 1.2 }, { label: '1500', val: 1.5 }].map(pw => (
                  <button key={pw.val} onClick={() => setPanelWidth(pw.val)}
                    className={`flex-1 py-3 rounded-xl font-semibold transition-all border-2 ${panelWidth === pw.val ? 'border-[#8b4444] bg-[#8b4444] text-white' : 'border-[#061d36]/20 text-[#061d36] hover:border-[#061d36]/40 bg-white'}`}
                    data-testid={`panel-width-${pw.label}`}>{pw.label} {t.panelWidthMm}</button>
                ))}
                <div className="flex-1 relative">
                  <input type="number" min="100" max="3000" step="10"
                    value={![1.0, 1.2, 1.5].includes(panelWidth) ? Math.round(panelWidth * 1000) : ''}
                    placeholder={language === 'ru' ? 'Другая' : 'Other'}
                    onChange={e => { const v = parseFloat(e.target.value); if (v >= 100 && v <= 3000) setPanelWidth(v / 1000); }}
                    className={`w-full h-full py-3 rounded-xl font-semibold text-center border-2 transition-all bg-white text-[#061d36] placeholder-[#061d36]/30 focus:outline-none ${![1.0, 1.2, 1.5].includes(panelWidth) ? 'border-[#8b4444] ring-2 ring-[#8b4444]/20' : 'border-[#061d36]/20 hover:border-[#061d36]/40'}`}
                    data-testid="panel-width-custom" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#061d36]/40">{t.panelWidthMm}</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <h3 className="text-[#061d36] font-medium mb-2 text-sm">{t.floorLabel}</h3>
                <input type="text" value={floorFilter} onChange={e => setFloorFilter(e.target.value)} placeholder={t.floorPlaceholder}
                  className="w-full px-4 py-2.5 bg-white border border-[#061d36]/20 rounded-xl text-[#061d36] text-sm placeholder-[#061d36]/30 focus:outline-none focus:border-[#8b4444]" />
              </div>
              <div>
                <h3 className="text-[#061d36] font-medium mb-2 text-sm">{t.roomTypeLabel}</h3>
                <input type="text" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} placeholder={t.roomTypePlaceholder}
                  className="w-full px-4 py-2.5 bg-white border border-[#061d36]/20 rounded-xl text-[#061d36] text-sm placeholder-[#061d36]/30 focus:outline-none focus:border-[#8b4444]" />
              </div>
            </div>

            <div>
              <h3 className="text-[#061d36] font-medium mb-2 text-sm">{t.reserveLabel} (%)</h3>
              <div className="flex gap-2">
                {[0, 5, 10, 15].map(pct => (
                  <button key={pct} onClick={() => setReserve(pct)} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all border-2 ${reserve === pct ? 'border-[#8b4444] bg-[#8b4444] text-white' : 'border-[#061d36]/20 text-[#061d36] hover:border-[#061d36]/40 bg-white'}`}>{pct}%</button>
                ))}
              </div>
            </div>

            <button onClick={handleCalculate} disabled={selectedFiles.length === 0 || loading}
              className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 ${selectedFiles.length === 0 || loading ? 'bg-[#061d36]/10 cursor-not-allowed text-[#061d36]/30' : 'bg-[#061d36] hover:bg-[#0d2a47] text-white shadow-lg'}`}
              data-testid="calculate-btn">
              {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />{t.calculating}</>) : (<><Calculator className="w-5 h-5" />{t.calculate}</>)}
            </button>

            {error && <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4"><AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" /><p className="text-red-700 text-sm">{error}</p></div>}
          </div>

          {/* Right: Editable Results */}
          <div>
            {calculated ? (
              <div className="space-y-4" data-testid="calc-results">
                {/* Summary */}
                <div className="bg-[#061d36] rounded-2xl p-6 text-white">
                  <h3 className="font-bold text-lg mb-4">{t.totals}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-4">
                      <Layers className="w-5 h-5 text-[#8b4444] mb-2" />
                      <p className="text-2xl font-bold">{calculated.totals.total_panels}</p>
                      <p className="text-white/60 text-sm">{t.totalPanels}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <Square className="w-5 h-5 text-[#8b4444] mb-2" />
                      <p className="text-2xl font-bold">{calculated.totals.total_net_area_m2}</p>
                      <p className="text-white/60 text-sm">{t.netArea} ({t.m2})</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <CornerDownRight className="w-5 h-5 text-[#8b4444] mb-2" />
                      <p className="text-lg font-bold">{calculated.totals.joint_profiles_pcs} {t.pcs}</p>
                      <p className="text-white/60 text-sm">{t.jointProfiles}</p>
                      <p className="text-white/30 text-xs mt-1">{calculated.totals.joint_profiles_m} {t.m}</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <Building2 className="w-5 h-5 text-[#8b4444] mb-2" />
                      <p className="text-lg font-bold">{calculated.totals.corner_profiles_inner} {t.pcs}</p>
                      <p className="text-white/60 text-sm">{t.cornerProfiles}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
                    <div><p className="text-white/40 text-xs">{t.plinth}</p><p className="text-white font-semibold">{calculated.totals.plinth_pcs} {t.pcs}<span className="text-white/30 text-xs ml-2">{calculated.totals.plinth_m} {t.m}</span></p></div>
                    <div><p className="text-white/40 text-xs">{t.topMolding}</p><p className="text-white font-semibold">{calculated.totals.top_molding_pcs} {t.pcs}<span className="text-white/30 text-xs ml-2">{calculated.totals.top_molding_m} {t.m}</span></p></div>
                  </div>
                  {reserve > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#8b4444]/30 bg-[#8b4444]/10 rounded-xl p-4 -mx-1">
                      <p className="text-[#8b4444] font-semibold text-sm mb-2">{t.totalWithReserve} (+{reserve}%):</p>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div><p className="text-white font-bold text-lg">{calculated.totals.total_panels + Math.ceil(calculated.totals.total_panels * reserve / 100)}</p><p className="text-white/40 text-xs">{t.totalPanels}</p></div>
                        <div><p className="text-white font-bold">{calculated.totals.joint_profiles_pcs + Math.ceil(calculated.totals.joint_profiles_pcs * reserve / 100)} {t.pcs}</p><p className="text-white/40 text-xs">{t.jointProfiles}</p></div>
                        <div><p className="text-white font-bold">{calculated.totals.plinth_pcs + Math.ceil(calculated.totals.plinth_pcs * reserve / 100)} {t.pcs}</p><p className="text-white/40 text-xs">{t.plinth}</p></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Editable hint */}
                <div className="flex items-center gap-2 text-xs text-[#061d36]/50 bg-white rounded-lg px-3 py-2">
                  <Edit3 className="w-3.5 h-3.5" />
                  <span>{t.editHint}</span>
                </div>

                {/* Editable Rooms */}
                {calculated.rooms.map((room, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-5 border-l-4 border border-[#061d36]/10" style={{ borderLeftColor: ROOM_COLORS[idx % ROOM_COLORS.length] }}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: ROOM_COLORS[idx % ROOM_COLORS.length] }} />
                        <EditableCell value={rooms[idx]?.name || room.name} onChange={v => updateRoom(idx, 'name', v)} type="text" className="font-bold text-[#061d36]" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-[#061d36]/40">{t.ceilingH}:</span>
                        <EditableCell value={rooms[idx]?.ceiling_height_m || panelHeight} onChange={v => updateRoom(idx, 'ceiling_height_m', v)} step={0.1} className="text-sm text-[#061d36]" />
                        <span className="text-xs text-[#061d36]/40">{t.m}</span>
                        <button onClick={() => deleteRoom(idx)} className="ml-2 text-red-400 hover:text-red-600 p-1 rounded" title={t.deleteRoom}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-center text-sm mb-3">
                      <div className="bg-[#F5F5F0] rounded-lg p-2"><p className="font-bold text-[#061d36]">{room.net_area_m2}</p><p className="text-[#061d36]/40 text-xs">{t.netArea} ({t.m2})</p></div>
                      <div className="bg-[#F5F5F0] rounded-lg p-2"><p className="font-bold text-[#8b4444]">{room.panels_full}</p><p className="text-[#061d36]/40 text-xs">{t.panelsFull}</p></div>
                      <div className="bg-[#F5F5F0] rounded-lg p-2"><p className="font-bold text-[#061d36]">{room.panel_layout.map(h => `${h}м`).join('+')}</p><p className="text-[#061d36]/40 text-xs">{t.panelLayout}</p></div>
                    </div>

                    {/* Editable walls */}
                    <div className="space-y-1">
                      {room.walls.map((wall, wi) => (
                        <div key={wi} className="flex items-center gap-2 text-xs bg-[#F5F5F0] rounded-lg px-3 py-2">
                          <span className="text-[#061d36]/40 w-6">#{wi+1}</span>
                          <EditableCell value={rooms[idx]?.walls[wi]?.length_m || wall.length_m} onChange={v => updateWall(idx, wi, 'length_m', v)} step={0.01} className="font-medium text-[#061d36]" />
                          <span className="text-[#061d36]/40">{t.m}</span>
                          <span className="text-[#061d36]/20">|</span>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={rooms[idx]?.walls[wi]?.has_door || false} onChange={e => updateWall(idx, wi, 'has_door', e.target.checked)} className="w-3.5 h-3.5 accent-[#8b4444]" />
                            <span className="text-[#061d36]/60">{t.door}</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <input type="checkbox" checked={rooms[idx]?.walls[wi]?.has_window || false} onChange={e => updateWall(idx, wi, 'has_window', e.target.checked)} className="w-3.5 h-3.5 accent-[#2980b9]" />
                            <span className="text-[#061d36]/60">{t.window}</span>
                          </label>
                          <span className="ml-auto font-medium text-[#8b4444]">{wall.panels} п.</span>
                          {room.walls.length > 1 && <button onClick={() => deleteWall(idx, wi)} className="text-red-300 hover:text-red-500 p-0.5"><Trash2 className="w-3 h-3" /></button>}
                        </div>
                      ))}
                      <button onClick={() => addWall(idx)} className="text-xs text-[#8b4444] hover:text-[#7a3838] font-medium px-3 py-1">{t.addWall}</button>
                    </div>
                  </div>
                ))}

                {/* Add room button */}
                <button onClick={addRoom} className="w-full py-3 rounded-xl border-2 border-dashed border-[#061d36]/20 text-[#061d36]/50 hover:border-[#8b4444] hover:text-[#8b4444] transition-all flex items-center justify-center gap-2 text-sm font-medium">
                  <Plus className="w-4 h-4" /> {t.addRoom}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border-2 border-dashed border-[#061d36]/10 min-h-[400px] flex items-center justify-center">
                <div className="text-center p-8">
                  <Calculator className="w-12 h-12 text-[#061d36]/10 mx-auto mb-4" />
                  <p className="text-[#061d36]/30 text-sm">{language === 'ru' ? 'Загрузите чертёж и нажмите "Рассчитать"' : 'Upload and click Calculate'}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PanelCalculator;
